"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = CountdownClock;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _react2 = require("@linaria/react");
var _intents = _interopRequireDefault(require("@site/pattern-library-utils/intents"));
var _density = require("@site/pattern-library-utils/density");
var _activeCountdown = _interopRequireDefault(require("./active-countdown"));
var _expiredCountdown = _interopRequireDefault(require("./expired-countdown"));
var _button = _interopRequireDefault(require("../merch-button/button.js"));
var _spinner = _interopRequireDefault(require("@ux/spinner"));
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const CountdownClockContainer = /*#__PURE__*/(0, _react2.styled)("div")({
  name: "CountdownClockContainer",
  class: "core-c1kuosky"
});
const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;
const UTC_TIME_ZONE_REGEX = /(?:Z|[+-](?:2[0-3]|[01][0-9]):?[0-5][0-9])$/;
const getISODate = expirationTime => {
  const defaultTimeZone = '-0700'; // Pacific DaylightTime
  expirationTime = expirationTime.trim().replace(' ', 'T').replace(' GMT', '');
  if (!expirationTime.match(UTC_TIME_ZONE_REGEX)) {
    expirationTime += defaultTimeZone;
  }
  return new Date(new Date(expirationTime).toUTCString()).getTime();
};
const addLeadingZero = timeValue => timeValue < 10 ? `0${timeValue}` : timeValue;

/**
* Render Countdown Clock
* @module CountdownClock
*
* @param {object} props React props for CountdownClock element
* @param {boolean} props.invert Invert time blocks' colors
* @param {string} props.daysLabel Label for days time block
* @param {string} props.hoursLabel Label for hours time block
* @param {string} props.minutesLabel Label for minutes time block
* @param {string} props.secondsLabel Label for seconds time block
* @param {string} props.activeCountdownTitle Active countdown's title
* @param {string} props.expiredCountdownTitle Expired countdown's title
* @param {string} props.expirationTime Date and time when countdown expires
* @param {object} props.afterExpirationButton Button to appear after countdown expiration
 * @param {String} props.partial Theme partial
* @returns {React.ReactElement} Countdown Clock component
* @public
*/
function CountdownClock({
  invert = false,
  activeCountdownTitle,
  expiredCountdownTitle,
  expirationTime,
  afterExpirationButton,
  partial,
  ...timeLabelProps
}) {
  const {
    daysLabel,
    hoursLabel,
    minutesLabel,
    secondsLabel
  } = timeLabelProps;
  let interval;
  const ISODate = getISODate(expirationTime);
  const [countdownDate] = (0, _react.useState)(ISODate);
  const [time, setTime] = (0, _react.useState)({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [countdownStatus, setCountdownStatus] = (0, _react.useState)(null);
  const [alertMessage, setAlertMessage] = (0, _react.useState)();
  const getTimeComponents = timeDifference => {
    const days = Math.floor(timeDifference / MS_PER_DAY);
    const hours = Math.floor(timeDifference % MS_PER_DAY / MS_PER_HOUR);
    let minutes = Math.floor(timeDifference % MS_PER_HOUR / MS_PER_MINUTE);
    let seconds = Math.floor(timeDifference % MS_PER_MINUTE / MS_PER_SECOND);
    if (seconds === 0) {
      const message = `${activeCountdownTitle} ` + `${days} ${daysLabel} ${hours} ${hoursLabel} ${minutes} ${minutesLabel} ${seconds} ${secondsLabel}`;
      setAlertMessage(message);
    }
    minutes = addLeadingZero(minutes);
    seconds = addLeadingZero(seconds);
    setTime({
      days,
      hours,
      minutes,
      seconds
    });
  };
  const updateTime = () => {
    if (countdownDate) {
      const currentTime = new Date(new Date().toUTCString()).getTime();
      const timeDifference = countdownDate - currentTime;
      if (timeDifference < 0) {
        clearInterval(interval);
        setCountdownStatus('expired');
      } else {
        countdownStatus === null && setCountdownStatus('active');
        getTimeComponents(timeDifference);
      }
    }
  };
  (0, _react.useEffect)(() => {
    interval = setInterval(() => updateTime(), 1000);
    return () => clearInterval(interval);
  }, []);
  const spinner = /*#__PURE__*/_react.default.createElement(_spinner.default, null);
  return /*#__PURE__*/_react.default.createElement(CountdownClockContainer, {
    className: "countdown-clock",
    "data-cy": "countdown-clock",
    "data-partial": partial || (invert ? 'dark1' : '')
  }, countdownStatus === 'expired' ? /*#__PURE__*/_react.default.createElement(_expiredCountdown.default, {
    title: expiredCountdownTitle,
    afterExpirationButton: afterExpirationButton
  }) : /*#__PURE__*/_react.default.createElement(_activeCountdown.default, (0, _extends2.default)({
    title: activeCountdownTitle,
    time: countdownStatus === 'active' ? time : {
      days: spinner,
      hours: spinner,
      minutes: spinner,
      seconds: spinner
    },
    alertMessage: alertMessage,
    active: countdownStatus === 'active'
  }, timeLabelProps)));
}
CountdownClock.propTypes = {
  invert: _propTypes.default.bool,
  daysLabel: _propTypes.default.string.isRequired,
  hoursLabel: _propTypes.default.string.isRequired,
  minutesLabel: _propTypes.default.string.isRequired,
  secondsLabel: _propTypes.default.string.isRequired,
  activeCountdownTitle: _propTypes.default.string,
  expiredCountdownTitle: _propTypes.default.string,
  expirationTime: _propTypes.default.string.isRequired,
  afterExpirationButton: _propTypes.default.shape(_button.default.propTypes),
  partial: _propTypes.default.string
};